import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import TechStack from "../components/about/techStack";

import SEO from "../data/seo";
import { useTheme } from "../contexts/ThemeContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	position: relative;
	overflow-x: hidden;
	transition: background-color 0.3s ease;
`;

const ContentWrapper = styled.div<{ theme: Theme }>`
	max-width: ${props => props.theme.density.contentMaxWidth};
	margin: 0 auto;
	padding: 0 ${props => props.theme.density.pageGutter};
	position: relative;
	z-index: 10;

	@media (max-width: 768px) {
		padding: 0 ${props => props.theme.density.pageGutterMobile};
	}
`;

const AboutContainer = styled.div`
	padding: 1.25rem 0 0;
	animation: ${fadeInUp} 0.3s ease-out 0.1s both;
`;

const AboutContent = styled.div<{ theme: Theme }>`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: clamp(1.5rem, 4vw, 2.5rem);
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.borderRadius['2xl']};
	box-shadow: ${props => props.theme.shadows.base};
`;

const Eyebrow = styled.p<{ theme: Theme }>`
	margin: 0;
	color: ${props => props.theme.colors.primary.main};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: 650;
	letter-spacing: 0.08em;
	text-transform: uppercase;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	color: ${props => props.theme.colors.text.primary};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	margin: 0;
	animation: ${fadeInUp} 0.3s ease-out 0.1s both;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const Subtitle = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	line-height: ${props => props.theme.typography.roles.body.lineHeight};
	color: ${props => props.theme.colors.text.secondary};
	margin: 0;
	max-width: 860px;
	white-space: pre-line;
	animation: ${fadeInUp} 0.3s ease-out 0.2s both;

	@media (max-width: 768px) {
		font-size: 1rem;
	}
`;

const RoleList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin-top: 0.25rem;
`;

const Role = styled.span<{ theme: Theme }>`
	padding: 0.4rem 0.7rem;
	color: ${props => props.theme.colors.text.secondary};
	background: ${props => props.theme.colors.background.tertiary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.borderRadius.full};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
`;

const TechStackWrapper = styled.div`
	margin-top: 3rem;
	animation: ${fadeInUp} 0.3s ease-out 0.3s both;
`;

const FooterWrapper = styled.div`
	margin-top: 2.5rem;
	padding: 0.75rem 0;
`;

const About = () => {
	const { theme } = useTheme();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "about")!;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`About | ${userInfo.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar active="about" />
				<ContentWrapper theme={theme}>
					<AboutContainer>
						<AboutContent theme={theme}>
							<Eyebrow theme={theme}>About / Profile</Eyebrow>
							<Title theme={theme}>{userInfo.about.title}</Title>
							<Subtitle theme={theme}>{userInfo.about.description}</Subtitle>
							<RoleList>
								<Role theme={theme}>iOS Engineering</Role>
								<Role theme={theme}>Architecture</Role>
								<Role theme={theme}>Backend &amp; Cloud</Role>
								<Role theme={theme}>Independent Products</Role>
							</RoleList>
						</AboutContent>
					</AboutContainer>

					<TechStackWrapper>
						<TechStack />
					</TechStackWrapper>

					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</ContentWrapper>
			</PageContainer>
		</React.Fragment>
	);
};

export default About;
